import * as Sentry from '@sentry/nextjs'

export interface LocalStorageCoupon {
  code: string
  discountAmount?: number
  discountPercent?: number
  expiryDate?: string
}

export const COUPONS_UPDATED_EVENT = 'coupons-updated'

export const isStorageAvailable = () => {
  try {
    return !!window.localStorage
  } catch {
    return false
  }
}

const isIframe = typeof window !== 'undefined' && window.self !== window.top

const syncStorage = () => {
  window.parent.postMessage(
    {
      type: 'syncStorage',
      data: memoryStorage
    },
    '*'
  )
}

const memoryStorage: Record<string, string> = {}

export const safeStorage = {
  getItem: (key: string) => {
    try {
      if (isIframe) {
        return memoryStorage[key] ?? null
      }
      return localStorage.getItem(key)
    } catch (e) {
      return null
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      memoryStorage[key] = value
      syncStorage()
      localStorage.setItem(key, value)
    } catch {}
  },
  removeItem: (key: string): void => {
    try {
      memoryStorage[key] = null
      syncStorage()
      localStorage.removeItem(key)
    } catch {}
  }
}

export const AVAILABLE_COUPONS_KEY = 'availableCoupons'

interface CouponFormat {
  expirationTime: number
  coupons: Array<{ code: string } | string>
}

export const getAvailableCoupons = (): string[] => {
  try {
    const rawData = safeStorage.getItem(AVAILABLE_COUPONS_KEY)
    if (!rawData) return []

    const parsed = JSON.parse(rawData)
    const coupons = getCouponCodes(parsed)

    if (coupons.length === 0) {
      safeStorage.removeItem(AVAILABLE_COUPONS_KEY)
    }

    return coupons
  } catch {
    return []
  }
}

const getCouponCodes = (data: CouponFormat): string[] => {
  const isValid = data && 'expirationTime' in data && 'coupons' in data && data.expirationTime > Date.now() && data.coupons?.length > 0

  if (isValid) {
    return data.coupons.map(coupon => (typeof coupon === 'string' ? coupon : coupon.code)).filter((code): code is string => typeof code === 'string')
  }

  return []
}

export const HIDDEN_COUPON_KEY = 'hiddenCoupon'

interface HiddenCouponFormat {
  expirationTime: number
  code: string
}

// A referral coupon (e.g. Simply Health) applied silently — the code drives the
// discount but is never rendered anywhere in the UI.
export const setHiddenCoupon = (code: string, ttlMs: number = 60 * 60 * 1000): void => {
  try {
    safeStorage.setItem(HIDDEN_COUPON_KEY, JSON.stringify({ code, expirationTime: Date.now() + ttlMs }))
  } catch {}
}

export const getHiddenCoupon = (): string | null => {
  try {
    const raw = safeStorage.getItem(HIDDEN_COUPON_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as HiddenCouponFormat
    const isValid = parsed && typeof parsed.code === 'string' && parsed.code.trim().length > 0 && parsed.expirationTime > Date.now()
    if (!isValid) {
      safeStorage.removeItem(HIDDEN_COUPON_KEY)
      return null
    }

    return parsed.code
  } catch {
    return null
  }
}

export const getSuggestedCoupon = (): string | null => {
  const available = getAvailableCoupons()
  if (available.length > 0) {
    return available[0]
  }

  return null
}

export const REVIEW_CHECKOUTS_KEY = 'reviewCheckouts'

export interface ReviewCheckout {
  answer: string
  label: string
  option: string | null
  /** The renewal order the dose was chosen for, read when the review is submitted. Null if that read failed. */
  order?: string | null
}

type StoredReviewCheckouts = Record<string, ReviewCheckout & { expirationTime: number }>

const readReviewCheckouts = (): StoredReviewCheckouts => {
  try {
    const parsed = JSON.parse(safeStorage.getItem(REVIEW_CHECKOUTS_KEY) || '{}')

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const writeReviewCheckouts = (checkouts: StoredReviewCheckouts) => {
  const now = Date.now()
  const fresh = Object.fromEntries(Object.entries(checkouts).filter(([, checkout]) => checkout.expirationTime > now))

  if (Object.keys(fresh).length) safeStorage.setItem(REVIEW_CHECKOUTS_KEY, JSON.stringify(fresh))
  else safeStorage.removeItem(REVIEW_CHECKOUTS_KEY)
}

// A submitted review whose dose change is still unpaid, keyed by request hash, so its link resumes the checkout.
export const setReviewCheckout = (requestHash: string, checkout: ReviewCheckout, ttlMs: number = 30 * 24 * 60 * 60 * 1000): void => {
  writeReviewCheckouts({ ...readReviewCheckouts(), [requestHash]: { ...checkout, expirationTime: Date.now() + ttlMs } })
}

export const getReviewCheckout = (requestHash: string): ReviewCheckout | null => {
  const stored = readReviewCheckouts()[requestHash]
  if (!stored || stored.expirationTime <= Date.now() || typeof stored.answer !== 'string' || typeof stored.label !== 'string') return null

  return {
    answer: stored.answer,
    label: stored.label,
    option: typeof stored.option === 'string' ? stored.option : null,
    order: typeof stored.order === 'string' ? stored.order : null
  }
}

export const removeReviewCheckout = (requestHash: string): void => {
  const { [requestHash]: _removed, ...rest } = readReviewCheckouts()
  writeReviewCheckouts(rest)
}

export const REFERRAL_USER_ID_KEY = 'referralUserId'

interface ReferralUserIdFormat {
  expirationTime: number
  userId: string
}

// The partner's own id for a member who arrived through a referral link. It
// rides along with the order so the partner can be reported their members'
// utilisation. Same one hour window as a referral coupon, so an order never
// carries one without the other.
export const setReferralUserId = (userId: string, ttlMs: number = 60 * 60 * 1000): void => {
  try {
    safeStorage.setItem(REFERRAL_USER_ID_KEY, JSON.stringify({ userId, expirationTime: Date.now() + ttlMs }))
  } catch {}
}

export const getReferralUserId = (): string | null => {
  try {
    const raw = safeStorage.getItem(REFERRAL_USER_ID_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as ReferralUserIdFormat
    const isValid = parsed && typeof parsed.userId === 'string' && parsed.userId.trim().length > 0 && parsed.expirationTime > Date.now()
    if (!isValid) {
      safeStorage.removeItem(REFERRAL_USER_ID_KEY)
      return null
    }

    return parsed.userId
  } catch {
    return null
  }
}
